import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, fromEvent, map, Observable, startWith } from 'rxjs';
import { appActions } from '../state/actions';
import { basicLayoutActions } from '@app/layout/state/actions';

@Injectable({
  providedIn: 'root'
})
export class AdaptiveScreenService {
  private breakpoints: { [key: string]: string } = {
    xs: '(max-width: 599px)',
    sm: '(min-width: 600px) and (max-width: 1023px)',
    md: '(min-width: 1024px) and (max-width: 1279px)',
    lg: '(min-width: 1280px) and (max-width: 1919px)',
    xl: '(min-width: 1920px)',
  };

  private activeBreakpoint$ = new BehaviorSubject<string | null>(null);

  constructor(private store: Store) {
    this.detectBreakpoint();

    // Listen to resize events and update the active breakpoint
    fromEvent(window, 'resize')
      .pipe(
        map(() => this.getActiveBreakpoint()),
        startWith(this.getActiveBreakpoint())
      )
      .subscribe((breakpoint) => {
        this.activeBreakpoint$.next(breakpoint);
        this.dispatchBreakpointAction(breakpoint);
      });
  }

  private getActiveBreakpoint(): string | null {
    for (const [key, query] of Object.entries(this.breakpoints)) {
      if (window.matchMedia(query).matches) {
        return key;
      }
    }
    return null; // No breakpoint matched
  }

  private detectBreakpoint() {
    const currentBreakpoint = this.getActiveBreakpoint();
    this.activeBreakpoint$.next(currentBreakpoint);
    this.dispatchBreakpointAction(currentBreakpoint);
  }

  private dispatchBreakpointAction(breakpoint: string | null) {
    if (breakpoint === 'xs' || breakpoint === 'sm') {
      // Dispatch action for small screen
      this.store.dispatch(appActions.setSmallScreen({ isSmallScreen: true }));
    } else {
      // Dispatch action for large screen
      this.store.dispatch(appActions.setSmallScreen({ isSmallScreen: false }));
      this.store.dispatch(basicLayoutActions.setSidebarVisibility({ opened: false }));
    }
  }

  /**
   * Get an observable for the current active breakpoint
   */
  get activeBreakpoint(): Observable<string | null> {
    return this.activeBreakpoint$.asObservable();
  }

  /**
   * Check if a specific breakpoint is active
   * @param breakpoint Key of the breakpoint (e.g., 'sm', 'md', etc.)
   */
  isBreakpointActive(breakpoint: string): boolean {
    return window.matchMedia(this.breakpoints[breakpoint] || '').matches;
  }

}
